/**
 * Header navigation region: nav links for authenticated users,
 * login/signup buttons for unauthenticated users.
 *
 * @module components/HeaderNav
 */

import React                                      from 'react';
import { Link, useLocation }                      from 'react-router-dom';
import { CircleHelp, LayoutDashboard, Settings }  from 'lucide-react';
import { useAuth }                                from '../dataProviders/AuthProvider';
import { useI18n }                                from '../dataProviders/I18nProvider';
import UserContextMenu                            from './UserContextMenu';
import config                                     from '../config';

/**
 * Renders navigation controls in the right region of the header.
 *
 * @returns {React.ReactElement}
 */
export default function HeaderNav() {
  const { isAuthenticated } = useAuth();
  const { t }              = useI18n();
  const { pathname }       = useLocation();

  if (isAuthenticated) {
    const navLinks = [
      { to: config.pagePaths.help,      icon: CircleHelp,       label: t('nav.help') },
      { to: config.pagePaths.dashboard, icon: LayoutDashboard,  label: t('nav.dashboard') },
      { to: config.pagePaths.settings,  icon: Settings,         label: t('nav.settings') },
    ];

    return (
      <nav className="header-nav">
        {navLinks
          .filter(link => link.to !== pathname)
          .map(link => (
            <Link key={link.to} to={link.to} className="header-nav-link">
              <link.icon size={18} />
              <span>{link.label}</span>
            </Link>
          ))}
        <UserContextMenu />
      </nav>
    );
  }

  return (
    <nav className="header-nav">
      <Link to={config.pagePaths.login}>
        <button className="header-nav-button header-nav-button--secondary">
          {t('nav.login')}
        </button>
      </Link>
      <Link to={config.pagePaths.signup}>
        <button className="header-nav-button header-nav-button--primary">
          {t('nav.signup')}
        </button>
      </Link>
    </nav>
  );
}
